/**
 * Session and layout preferences.
 *
 * What belongs here: things the user chose about *the view*. Selected
 * symbol, timeframe, which panels are open, table density, chart
 * preferences, the ids selected for comparison.
 *
 * What does NOT belong here: anything the backend owns. Positions, prices,
 * runs, equity — all of that lives in React Query, and duplicating it into
 * Zustand is how a screen ends up showing a position that was closed four
 * minutes ago.
 *
 * The theme is also not here. It lives in `theme/ThemeProvider` because the
 * chart canvas has to read it synchronously, before React re-renders, and
 * because the anti-flash script in `index.html` writes it before any
 * JavaScript module has loaded. Two stores for the theme would be two
 * sources of truth.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { Timeframe } from '@/api/types';
import { demoSymbol, toDemo, toReal } from '@/lib/demoNames';

export type TableDensity = 'compact' | 'comfortable';
export type ChartCandleType = 'candle_solid' | 'candle_stroke' | 'ohlc' | 'area';

/**
 * Which launcher a live job belongs to. One job per surface: launching from
 * one panel replaces that panel's job, never another's. The four S2 exits are
 * four surfaces on purpose — the owner can put Base, Trail12, Safe and Greedy
 * in flight at once and each panel resumes its own.
 */
export type JobSurface =
  | 'backtest'
  | 's2'
  | 's2x-trail12'
  | 's2x-safe'
  | 's2x-greedy';

export interface ActiveJob {
  jobId: string;
  /** What the launcher said it was running — shown while resuming. */
  label: string;
  /** Epoch ms, for ordering and for ageing out stale entries defensively. */
  startedAt: number;
  /** Runs the launch started; >1 means a basket of separate results. */
  launchedCount: number;
}

/** Ids used to address dock panels, so a page can toggle one by name. */
export type DockTab = 'trades' | 'exits' | 'events' | 'params' | 'log' | 'monthly';

export interface UiState {
  // ── Instrument selection ────────────────────────────────────────────
  /** The symbol every chart-bearing page opens on. */
  symbol: string;
  timeframe: Timeframe;
  /** Recently viewed, newest first, capped at 8. Drives the quick switcher. */
  recentSymbols: string[];

  // ── Layout ──────────────────────────────────────────────────────────
  sidebarCollapsed: boolean;
  rightPanelOpen: boolean;
  /** Bottom dock height as a fraction of the viewport, 0.15–0.7. */
  dockRatio: number;
  dockOpen: boolean;
  activeDockTab: DockTab;
  density: TableDensity;

  // ── Chart preferences ───────────────────────────────────────────────
  candleType: ChartCandleType;
  showVolumePane: boolean;
  showGrid: boolean;
  /** Snap drawings to OHLC values. */
  magnetMode: boolean;
  /** Indicator registry keys pinned onto the chart, in draw order. */
  activeIndicators: string[];

  // ── Backtest workspace ──────────────────────────────────────────────
  /** Run ids selected for side-by-side comparison. Max 5 — the owner's
   *  stated ceiling, and past five columns a diff stops being readable. */
  comparisonRunIds: number[];
  /**
   * How many registry rows to fetch at a time.
   *
   * Persisted, following the symbol browser: a size the owner set is a
   * decision, and re-making it on every reload is the same annoyance as an
   * ordering that will not stay put. Ten by default — his words, "you can
   * show me the last ten trades, that is enough" — and the fetch is bounded
   * by it server-side, so the default costs one page rather than a
   * registry.
   */
  registryPageSize: number;
  /** The run whose report is open. */
  focusedRunId: number | null;

  /**
   * The account size the S2 Base panel runs at — the toggle offers €20k or
   * €50k. Held here rather than in the panel so the record document and the
   * workspace header (siblings of the panel) track the toggle, and persisted
   * so the choice survives a reload the way a live run does. Both are measured
   * 8/8-green records; €20k is the default (highest return per euro).
   */
  s2BaseCapital: number;

  // ── Ephemeral cross-component selection ─────────────────────────────
  //
  // Deliberately NOT persisted. This is the seam that cross-links the
  // chart, the equity curve and the trade table: hovering a row pulses a
  // marker, clicking one scrolls the chart, brushing the equity curve
  // filters the table. Threading it through props would mean every page
  // re-implementing it slightly differently.
  hoveredTradeId: string | null;
  selectedTradeId: string | null;
  /** [startEpochSeconds, endEpochSeconds] set by an equity-curve brush. */
  rangeFilter: [number, number] | null;

  // ── Runs in flight ──────────────────────────────────────────────────
  //
  // The backend job keeps running when its page unmounts; this is where the
  // FRONTEND remembers that it did. `jobId` used to be component state in
  // three panels, so leaving the page and coming back showed a blank
  // launcher over a live job — which the owner read, reasonably, as "my run
  // was cancelled". One entry per launcher surface: a panel resumes its own
  // job on mount and follows it to a terminal state.
  //
  // Persisted, so a full reload resumes a live run too. Safe only because
  // every launcher pairs the resume with a stale-job guard: an id the
  // server no longer reports (restart, last week's session) is cleared with
  // a plain word instead of spinning forever.
  activeJobs: Record<string, ActiveJob>;

  // ── Actions ─────────────────────────────────────────────────────────
  setSymbol: (symbol: string) => void;
  setTimeframe: (timeframe: Timeframe) => void;
  setInstrument: (symbol: string, timeframe: Timeframe) => void;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleRightPanel: () => void;
  setRightPanelOpen: (open: boolean) => void;
  setDockRatio: (ratio: number) => void;
  setDockOpen: (open: boolean) => void;
  setActiveDockTab: (tab: DockTab) => void;
  setDensity: (density: TableDensity) => void;

  setCandleType: (type: ChartCandleType) => void;
  toggleVolumePane: () => void;
  toggleGrid: () => void;
  toggleMagnetMode: () => void;
  addIndicator: (key: string) => void;
  removeIndicator: (key: string) => void;
  setIndicators: (keys: string[]) => void;

  toggleComparisonRun: (runId: number) => void;
  setRegistryPageSize: (size: number) => void;
  clearComparison: () => void;
  setFocusedRunId: (runId: number | null) => void;
  /** Set the S2 Base account size (the €20k / €50k toggle). */
  setS2BaseCapital: (capital: number) => void;

  setHoveredTrade: (tradeId: string | null) => void;
  setSelectedTrade: (tradeId: string | null) => void;
  setRangeFilter: (range: [number, number] | null) => void;
  clearSelection: () => void;

  /** Record the job a surface just launched, replacing its previous one. */
  registerJob: (surface: JobSurface, job: ActiveJob) => void;
  /** Forget a surface's job once it reaches a terminal state. */
  clearJob: (surface: JobSurface) => void;
}

const MAX_RECENT = 8;
/** The owner compares "three or four or five" runs; the backend allows 12,
 *  but five columns is where a parameter diff stops being one glance. */
const MAX_COMPARISON = 5;

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      symbol: demoSymbol('BTCUSDT'),
      timeframe: '1h',
      recentSymbols: [demoSymbol('BTCUSDT')],

      sidebarCollapsed: false,
      rightPanelOpen: true,
      dockRatio: 0.32,
      dockOpen: true,
      activeDockTab: 'trades',
      density: 'compact',

      candleType: 'candle_solid',
      showVolumePane: true,
      showGrid: true,
      magnetMode: false,
      activeIndicators: [],

      comparisonRunIds: [],
      registryPageSize: 10,
      focusedRunId: null,
      s2BaseCapital: 20_000,

      hoveredTradeId: null,
      selectedTradeId: null,
      rangeFilter: null,
      activeJobs: {},

      setSymbol: (symbol) =>
        set((state) => {
          const next = symbol.toUpperCase();
          if (next === state.symbol) return state;
          return {
            symbol: next,
            recentSymbols: [next, ...state.recentSymbols.filter((s) => s !== next)].slice(
              0,
              MAX_RECENT,
            ),
            // Changing instrument invalidates any cross-link selection: a
            // trade id from BTCUSDT means nothing on the ETHUSDT chart.
            hoveredTradeId: null,
            selectedTradeId: null,
            rangeFilter: null,
          };
        }),

      setTimeframe: (timeframe) => set({ timeframe, rangeFilter: null }),

      setInstrument: (symbol, timeframe) =>
        set((state) => {
          const next = symbol.toUpperCase();
          return {
            symbol: next,
            timeframe,
            recentSymbols: [next, ...state.recentSymbols.filter((s) => s !== next)].slice(
              0,
              MAX_RECENT,
            ),
            hoveredTradeId: null,
            selectedTradeId: null,
            rangeFilter: null,
          };
        }),

      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleRightPanel: () => set((state) => ({ rightPanelOpen: !state.rightPanelOpen })),
      setRightPanelOpen: (rightPanelOpen) => set({ rightPanelOpen }),
      setDockRatio: (ratio) => set({ dockRatio: Math.min(0.7, Math.max(0.15, ratio)) }),
      setDockOpen: (dockOpen) => set({ dockOpen }),
      setActiveDockTab: (activeDockTab) => set({ activeDockTab, dockOpen: true }),
      setDensity: (density) => set({ density }),

      setCandleType: (candleType) => set({ candleType }),
      toggleVolumePane: () => set((state) => ({ showVolumePane: !state.showVolumePane })),
      toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
      toggleMagnetMode: () => set((state) => ({ magnetMode: !state.magnetMode })),

      addIndicator: (key) =>
        set((state) =>
          state.activeIndicators.includes(key)
            ? state
            : { activeIndicators: [...state.activeIndicators, key] },
        ),
      removeIndicator: (key) =>
        set((state) => ({
          activeIndicators: state.activeIndicators.filter((k) => k !== key),
        })),
      setIndicators: (activeIndicators) => set({ activeIndicators }),

      toggleComparisonRun: (runId) =>
        set((state) => {
          if (state.comparisonRunIds.includes(runId)) {
            return { comparisonRunIds: state.comparisonRunIds.filter((id) => id !== runId) };
          }
          if (state.comparisonRunIds.length >= MAX_COMPARISON) return state;
          return { comparisonRunIds: [...state.comparisonRunIds, runId] };
        }),
      clearComparison: () => set({ comparisonRunIds: [] }),
      setRegistryPageSize: (registryPageSize) => set({ registryPageSize }),
      setFocusedRunId: (focusedRunId) =>
        set({ focusedRunId, hoveredTradeId: null, selectedTradeId: null, rangeFilter: null }),
      setS2BaseCapital: (s2BaseCapital) => set({ s2BaseCapital }),

      setHoveredTrade: (hoveredTradeId) => set({ hoveredTradeId }),
      setSelectedTrade: (selectedTradeId) => set({ selectedTradeId }),
      setRangeFilter: (rangeFilter) => set({ rangeFilter }),
      clearSelection: () =>
        set({ hoveredTradeId: null, selectedTradeId: null, rangeFilter: null }),

      registerJob: (surface, job) =>
        set((state) => ({ activeJobs: { ...state.activeJobs, [surface]: job } })),
      clearJob: (surface) =>
        set((state) => {
          if (!(surface in state.activeJobs)) return state;
          const next = { ...state.activeJobs };
          delete next[surface];
          return { activeJobs: next };
        }),
    }),
    {
      name: 'bheema.ui',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // Demo display names (lib/demoNames): a saved ticker is shown by its demo name.
      // Stored as REAL tickers (partialize), shown by demo name; also repairs a
      // browser that saved a demo name before.
      merge: (persisted, current) => ({ ...current, ...toDemo(toReal((persisted ?? {}) as Partial<UiState>)) }),
      // Persist preferences only. The ephemeral selection fields are
      // deliberately excluded — restoring a hovered trade id from last week
      // would highlight a row that is no longer on screen.
      partialize: (state) => ({
        // Live jobs persist so a full RELOAD also resumes them; every
        // launcher carries a stale-job guard that clears an id the server
        // no longer reports, so last week's job cannot spin forever.
        activeJobs: state.activeJobs,
        symbol: toReal(state.symbol),
        timeframe: state.timeframe,
        recentSymbols: toReal(state.recentSymbols),
        sidebarCollapsed: state.sidebarCollapsed,
        rightPanelOpen: state.rightPanelOpen,
        dockRatio: state.dockRatio,
        dockOpen: state.dockOpen,
        activeDockTab: state.activeDockTab,
        density: state.density,
        candleType: state.candleType,
        showVolumePane: state.showVolumePane,
        showGrid: state.showGrid,
        magnetMode: state.magnetMode,
        activeIndicators: state.activeIndicators,
        registryPageSize: state.registryPageSize,
        s2BaseCapital: state.s2BaseCapital,
      }),
    },
  ),
);

// ── Selectors ─────────────────────────────────────────────────────────
//
// Narrow subscriptions. `useUiStore((s) => s.symbol)` re-renders only when
// the symbol changes; subscribing to the whole store re-renders the chart
// every time a table row is hovered.

export const useSymbol = (): string => useUiStore((s) => s.symbol);
export const useTimeframe = (): Timeframe => useUiStore((s) => s.timeframe);
export const useDensity = (): TableDensity => useUiStore((s) => s.density);
export const useHoveredTradeId = (): string | null => useUiStore((s) => s.hoveredTradeId);
export const useSelectedTradeId = (): string | null => useUiStore((s) => s.selectedTradeId);
export const useRangeFilter = (): [number, number] | null =>
  useUiStore((s) => s.rangeFilter);

/** Row height in pixels for the current density. Tables read this. */
export function rowHeightFor(density: TableDensity): number {
  return density === 'compact' ? 24 : 30;
}
